/**
 * A fingerprint of everything a run's outcome depends on.
 *
 * `SIM_VERSION` is the contract that says "client and server agree about the rules", and
 * it works — when it is remembered. The failure it cannot catch is the one that actually
 * happens: content changes and the version does not, so both sides report the same
 * version while generating different maps, rolling different waves, or valuing the same
 * tower differently. Every submission then fails as a mismatch, and nothing anywhere says
 * why, because the one number both sides publish agrees.
 *
 * This is that missing number. It is derived, so it cannot be forgotten: change a tower's
 * damage and it moves on its own.
 *
 * NOT a determinism input — nothing in the simulation reads it. It exists to be compared.
 */
import { fnv1a64 } from '@prism/sim';
import { BALANCE } from './data/balance.js';
import { CLASSES } from './data/classes.js';
import { ENEMIES } from './data/enemies.js';
import { GEN_DATA } from './data/gen.js';
import { TOWERS } from './data/towers.js';
import { WAVES } from './data/waves.js';
import { MAP_SOURCES } from './data/maps.js';

/**
 * Hex, 16 characters.
 *
 * Computed once at module load over the authored data in a fixed order. `JSON.stringify`
 * on a frozen array of plain objects is stable within a build, which is all this needs to
 * be — it compares one build against another, never against itself over time.
 */
export const CONTENT_HASH: string = fnv1a64(
  JSON.stringify([TOWERS, ENEMIES, CLASSES, WAVES, MAP_SOURCES, GEN_DATA, BALANCE]),
)
  .toString(16)
  .padStart(16, '0');
